import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CalendarDays, MessageSquareText, Home, Users, TrendingUp, ArrowRight, Star, CheckCircle2, RefreshCw, Sparkles, Wrench, FileText, Receipt, UserCog, LayoutDashboard } from 'lucide-react';

const LOGO_URL = '/logo_transparent.png';

const BG_HERO = 'https://images.unsplash.com/photo-1757439402190-99b73ac8e807?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodCUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzc2MTcyMjY2fDA&ixlib=rb-4.1.0&q=85';
const BG_STEPS = 'https://images.unsplash.com/photo-1772475385317-09f9ef320474?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodCUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzc2MTcyMjY2fDA&ixlib=rb-4.1.0&q=85';
const BG_TESTIMONIALS = 'https://images.unsplash.com/photo-1759472659432-3232e42d04d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2YWNhdGlvbiUyMHJlbnRhbCUyMGJlZHJvb20lMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzYxNzIyNzN8MA&ixlib=rb-4.1.0&q=85';
const BG_CTA = 'https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodCUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzc2MTcyMjY2fDA&ixlib=rb-4.1.0&q=85';

const FEATURES = [
  { icon: CalendarDays, title: 'Unified Booking Calendar', desc: 'A colour-coded timeline of every booking across all your properties. Instantly see who is checking in and out, spot gaps, and add manual bookings in seconds.' },
  { icon: RefreshCw, title: 'Automatic iCal Sync', desc: 'Connect your Airbnb, Booking.com, and Vrbo calendars once — RentalHub syncs them automatically in the background, deduplicates entries, and keeps you double-booking free.' },
  { icon: Home, title: 'Properties & Units', desc: 'Organise your entire portfolio by property and unit, complete with bedrooms, nightly rates, guest capacity, and per-unit calendar connections.' },
  { icon: LayoutDashboard, title: 'Live Dashboard', desc: 'Occupancy, revenue, upcoming check-ins and check-outs at a glance — interactive charts give you the health of your rental business the moment you log in.' },
  { icon: Users, title: 'Guest Profiles', desc: 'A built-in guest database that grows automatically from your bookings, so contact details and stay history are always one tap away.' },
  { icon: Sparkles, title: 'Cleaning Scheduling', desc: 'Create cleaning jobs for every turnover, assign them to your cleaners, and track each job from scheduled to done — no more missed changeovers.' },
  { icon: Wrench, title: 'Maintenance Tickets', desc: 'Log issues the moment they appear, assign them to maintenance staff, set priorities, and follow every ticket through to resolution.' },
  { icon: MessageSquareText, title: 'Guest Messaging', desc: 'Keep all guest communication in one inbox with AI-powered reply suggestions to respond quickly and professionally.' },
  { icon: TrendingUp, title: 'Financial Tracking', desc: 'Record income and expenses per property and unit, track configurable service fees, and always know exactly what each rental earns.' },
  { icon: Receipt, title: 'Invoice Generation', desc: 'Generate clean, professional invoices for owners and guests directly from your booking and financial data.' },
  { icon: FileText, title: 'Reports & Exports', desc: 'Occupancy rates, revenue breakdowns, and performance trends — exportable to CSV or PDF for your accountant or property owners.' },
  { icon: UserCog, title: 'Team Roles & Access', desc: 'Invite managers, cleaners, and maintenance staff with their own logins. Role-based access means everyone sees exactly what they need — nothing more.' },
];

const STEPS = [
  { num: '1', title: 'Sign Up', desc: 'Create your free account in under a minute' },
  { num: '2', title: 'Add Properties', desc: 'Import your properties and connect iCal feeds' },
  { num: '3', title: 'Start Managing', desc: 'View all bookings, message guests, assign tasks' },
];

const STANDARD_FEATURES = ['Up to 5 properties', 'Basic reporting', 'iCal sync', '14-day free trial'];
const PRO_FEATURES = ['Unlimited properties', 'Advanced analytics', 'Priority iCal sync', 'AI messaging suggestions', 'Invoice generation', '14-day free trial'];

const TESTIMONIALS = [
  { quote: "Finally, an app that understands property managers! The iCal sync saves me hours every week.", name: 'Emma T.', role: 'Property Manager, London, UK' },
  { quote: "Managing 12 units used to be a nightmare. Now I do it all from my phone in minutes.", name: 'Michael R.', role: 'Airbnb Superhost, Sydney, Australia' },
  { quote: "The AI messaging suggestions are incredible. My response time improved from hours to minutes.", name: 'Sofia M.', role: 'Vacation Rental Owner, Spain' },
];

const FAQ_ITEMS = [
  { q: 'What is a short-term rental management app?', a: 'RentalHub is a platform that helps property managers and hosts manage bookings, guests, cleaning schedules, maintenance, and finances across multiple short-term rental platforms like Airbnb, Booking.com, and Vrbo - all from one dashboard.' },
  { q: 'How does the iCal sync work?', a: 'RentalHub connects to your Airbnb, Booking.com, and Vrbo calendars using the iCal protocol (RFC 5545). It automatically syncs bookings every 15 minutes so your calendar is always up to date and you avoid double bookings.' },
  { q: 'Can I manage multiple properties?', a: 'Yes! The Standard plan supports up to 5 properties, while the Pro plan offers unlimited properties. Each property can have multiple units, and you can manage them all from a single dashboard.' },
  { q: 'Is my data secure?', a: 'Absolutely. We use industry-standard encryption, secure authentication with JWT tokens, and your payment data is handled securely through Paystack. We never store your credit card information directly.' },
  { q: 'Can my cleaning and maintenance team use the app too?', a: 'Yes. You can invite managers, cleaners, and maintenance staff with their own logins. Cleaners see their assigned cleaning jobs, maintenance staff see their tickets, and managers get broader access - all controlled by role-based permissions.' },
  { q: 'Do you offer a free trial?', a: 'Yes, both plans come with a 14-day free trial. No credit card is required to start. You can explore all features and decide which plan works best for your needs.' },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* ───── HERO ───── */}
      <section
        className="relative min-h-screen flex items-center justify-center px-4"
        data-testid="hero-section"
      >
        <img src={BG_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px]" />
        <div className="relative z-10 text-center mx-auto" style={{ maxWidth: '90vw' }}>
          <div className="relative mx-auto mb-6" style={{ width: 'fit-content' }}>
            <img src={LOGO_URL} alt="RentalHub" className="select-none mx-auto block" data-testid="hero-logo" style={{ width: '100%', maxWidth: '1400px', height: 'auto' }} />
            <span className="absolute text-[0.6em] font-semibold text-slate-500 select-none" style={{ top: '38%', right: '15%' }}>TM</span>
          </div>

          <div className="flex justify-center mb-6">
            <span className="inline-block bg-slate-900 text-white text-sm font-semibold px-5 py-1.5 rounded-full" data-testid="hero-badge">
              Hassle-Free Hosting
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-5" data-testid="hero-heading">
            Manage Your Short-Term Rentals Like a Pro
          </h1>

          <p className="text-base sm:text-lg text-slate-900 font-medium max-w-xl mx-auto mb-8 leading-relaxed" style={{ textShadow: '0 1px 8px rgba(255,255,255,0.9)' }} data-testid="hero-subtitle">
            One app for your entire rental operation — synced calendars across Airbnb, Booking.com, and Vrbo, cleaning and maintenance scheduling, guest messaging, financials, invoices, and reports.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
            <Button
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3 text-base font-semibold rounded-full"
              onClick={() => navigate('/login?tab=register')}
              data-testid="hero-cta-trial"
            >
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-slate-400 text-slate-800 bg-white/70 backdrop-blur px-8 py-3 text-base font-semibold rounded-full"
              onClick={() => navigate('/login')}
              data-testid="hero-cta-signin"
            >
              Sign In
            </Button>
          </div>

          <p className="text-sm text-slate-500" data-testid="hero-trial-note">14-day free trial &bull; No credit card required</p>
        </div>
      </section>

      {/* ───── FEATURES ───── */}
      <section className="py-20 px-4 bg-white" data-testid="features-section">
        <div className="max-w-4xl mx-auto text-center mb-14">
          <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">Features</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Everything You Need to Manage Your Rentals</h2>
          <p className="text-base text-slate-500 mt-3 max-w-2xl mx-auto">From synced calendars to spotless turnovers and month-end reports — every part of your short-term rental business, in one place.</p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-testid={`feature-card-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section className="relative py-20 px-4" data-testid="how-it-works-section">
        <img src={BG_STEPS} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/70" />
        <div className="relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-14">
            <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Get Started in 3 Simple Steps</h2>
          </div>

          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10 text-center">
            {STEPS.map((s, i) => (
              <div key={i} data-testid={`step-${i}`}>
                <div className="w-14 h-14 bg-slate-900 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">{s.num}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── PRICING ───── */}
      <section className="py-20 px-4 bg-white" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto text-center mb-14">
          <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">Pricing</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-2">Simple, Transparent Pricing</h2>
          <p className="text-base text-slate-500">Choose the plan that fits your portfolio</p>
        </div>

        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="border border-slate-200 rounded-2xl p-8 text-center" data-testid="pricing-standard">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Standard</h3>
            <div className="mb-6">
              <span className="text-5xl font-extrabold text-slate-900">R250</span>
              <span className="text-base text-slate-500 ml-1">/month</span>
            </div>
            <ul className="space-y-3 mb-8 text-left">
              {STANDARD_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-slate-400 shrink-0" />{f}
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full py-3 text-base font-semibold rounded-lg border-slate-300"
              onClick={() => navigate('/login?tab=register')}
              data-testid="pricing-standard-btn"
            >
              Start Free Trial
            </Button>
          </div>

          <div className="border-2 border-slate-900 rounded-2xl p-8 text-center relative" data-testid="pricing-pro">
            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">Most Popular</span>
            <h3 className="text-lg font-bold text-slate-900 mb-3">Pro</h3>
            <div className="mb-6">
              <span className="text-5xl font-extrabold text-slate-900">R500</span>
              <span className="text-base text-slate-500 ml-1">/month</span>
            </div>
            <ul className="space-y-3 mb-8 text-left">
              {PRO_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-slate-400 shrink-0" />{f}
                </li>
              ))}
            </ul>
            <Button
              className="w-full bg-slate-900 hover:bg-slate-800 text-white py-3 text-base font-semibold rounded-lg"
              onClick={() => navigate('/login?tab=register')}
              data-testid="pricing-pro-btn"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </section>

      {/* ───── TESTIMONIALS ───── */}
      <section className="relative py-20 px-4" data-testid="testimonials-section">
        <img src={BG_TESTIMONIALS} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/65" />
        <div className="relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-14">
            <p className="text-sm font-bold text-slate-300 uppercase tracking-widest mb-3">Testimonials</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Loved by Property Managers</h2>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-sm text-white/90 mb-4 leading-relaxed italic">"{t.quote}"</p>
                <p className="text-sm font-bold text-white">{t.name}</p>
                <p className="text-xs text-white/60">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="py-20 px-4 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">FAQ</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Frequently Asked Questions</h2>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-slate-50 border-none rounded-lg px-5" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-sm font-semibold text-slate-800 hover:no-underline py-4">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-4">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ───── CTA BANNER ───── */}
      <section className="relative py-20 px-4" data-testid="cta-banner">
        <img src={BG_CTA} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">Ready to Simplify Your Rental Business?</h2>
          <p className="text-base text-slate-300 mb-8">Join hundreds of hosts who manage smarter.</p>
          <Button
            size="lg"
            className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-3 text-base font-semibold rounded-full"
            onClick={() => navigate('/login?tab=register')}
            data-testid="cta-banner-btn"
          >
            Start Your Free 14-Day Trial
          </Button>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer className="py-12 px-4 bg-white text-center" data-testid="landing-footer">
        <div className="relative mx-auto mb-3" style={{ width: 'fit-content' }}>
          <img src={LOGO_URL} alt="RentalHub" className="select-none mx-auto block" style={{ width: '100%', maxWidth: '1400px', height: 'auto' }} />
          <span className="absolute text-[0.6em] font-semibold text-slate-400 select-none" style={{ top: '38%', right: '15%' }}>TM</span>
        </div>
        <p className="text-sm text-slate-500 mb-4">Short-Term Rental Management Made Simple</p>
        <div className="flex items-center justify-center gap-4 text-sm text-slate-700 font-medium mb-6">
          <span className="hover:underline cursor-pointer" onClick={() => navigate('/blog')}>Blog</span>
          <span className="text-slate-300">&bull;</span>
          <span className="hover:underline cursor-pointer" onClick={() => navigate('/privacy')}>Privacy Policy</span>
          <span className="text-slate-300">&bull;</span>
          <span className="hover:underline cursor-pointer" onClick={() => navigate('/terms')}>Terms of Service</span>
        </div>
        <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} RentalHub. All rights reserved.</p>
      </footer>
    </div>
  );
}
